import React, { useMemo } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeRegular,
    XStack,
} from 'smoothie';

import { MemberProfileFlow } from '../common/Flow';
import { useFlow, withFlowProvider } from '../common/providers/FlowProvider';
import { FlowRouter, useFlowRouter } from '../common/providers/FlowRouter';
import type { DeepPartial } from '../common/types/DeepPartial';
import { FreelanceDetailsStep } from './steps/FreelanceDetailsStep';
import { FullTimeDetailsStep } from './steps/FullTimeDetailsStep';
import { IncomeSourceStep } from './steps/IncomeSourceStep';
import { OtherDetailsStep } from './steps/OtherDetailsStep';
import { PartTimeDetailsStep } from './steps/PartTimeDetailsStep';
import { type Income, IncomeType } from './types/Income';

export enum AddIncomeFlowSteps {
    INCOME_SOURCE = 'income_source',
    INCOME_FULL_TIME_DETAILS = 'income_full_time_details',
    INCOME_PART_TIME_DETAILS = 'income_part_time_details',
    INCOME_FREELANCE_DETAILS = 'income_freelance_details',
    INCOME_OTHER_DETAILS = 'income_other_details',
}

const getAddIncomeFlowRoutes = (data: DeepPartial<Income>) => {
    const shouldShowFullTimeDetails = data.type === IncomeType.FULL_TIME_SALARY;
    const shouldShowPartTimeDetails = data.type === IncomeType.PART_TIME_WAGE;
    const shouldShowFreelanceDetails = data.type === IncomeType.FREELANCE;
    const shouldShowOtherDetails =
        data.type === IncomeType.ALIMONY ||
        data.type === IncomeType.CHILD_SUPPORT ||
        data.type === IncomeType.OTHER;

    return [
        AddIncomeFlowSteps.INCOME_SOURCE,
        ...(shouldShowFullTimeDetails
            ? [AddIncomeFlowSteps.INCOME_FULL_TIME_DETAILS]
            : []),
        ...(shouldShowPartTimeDetails
            ? [AddIncomeFlowSteps.INCOME_PART_TIME_DETAILS]
            : []),
        ...(shouldShowFreelanceDetails
            ? [AddIncomeFlowSteps.INCOME_FREELANCE_DETAILS]
            : []),
        ...(shouldShowOtherDetails
            ? [AddIncomeFlowSteps.INCOME_OTHER_DETAILS]
            : []),
    ];
};

interface AddIncomeFlowProps {
    context?: 'onboarding' | 'settings';
}

export const AddIncomeFlow = withFlowProvider<Income, AddIncomeFlowProps>(
    ({ context = 'onboarding' }: AddIncomeFlowProps) => {
        const steps = useMemo(
            () => ({
                [AddIncomeFlowSteps.INCOME_SOURCE]: () => (
                    <IncomeSourceStep
                        shouldUseCurrentUserAsIncomeOwner={
                            context === 'settings'
                        }
                    />
                ),

                [AddIncomeFlowSteps.INCOME_FULL_TIME_DETAILS]: () => (
                    <FullTimeDetailsStep />
                ),
                [AddIncomeFlowSteps.INCOME_PART_TIME_DETAILS]: () => (
                    <PartTimeDetailsStep />
                ),
                [AddIncomeFlowSteps.INCOME_FREELANCE_DETAILS]: () => (
                    <FreelanceDetailsStep />
                ),
                [AddIncomeFlowSteps.INCOME_OTHER_DETAILS]: () => (
                    <OtherDetailsStep />
                ),
            }),
            [],
        );

        return (
            <FlowRouter steps={steps} getRoutes={getAddIncomeFlowRoutes}>
                <MemberProfileFlow tabBarOverride={<AddIncomeFlowTabBar />} />
            </FlowRouter>
        );
    },
);

const AddIncomeFlowTabBar = () => {
    const { data, index } = useFlow<Income>();
    const { onBack, routes } = useFlowRouter<Income, AddIncomeFlowSteps>();

    const getScreenTitle = (routeKey: string): string => {
        if (routeKey === (AddIncomeFlowSteps.INCOME_SOURCE as string)) {
            return 'Add income';
        }

        return data.description ?? 'Income details';
    };

    const title = getScreenTitle(routes[index]?.key ?? '');

    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            marginTop={8}
            marginBottom={16}
            paddingHorizontal={8}
            $sm={{ paddingHorizontal: 0 }}
        >
            <XStack width={40}>
                <BackButton
                    iconType={BackButtonIconType.ARROW_LEFT}
                    customBackFn={onBack}
                />
            </XStack>
            <BodyLargeRegular>{title}</BodyLargeRegular>
            <XStack width={40} />
        </XStack>
    );
};
